import { Marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Markdown → safe HTML for the help pages.
 *
 * The content is our own (bundled from the repo), but it is still sanitised:
 * it is injected as HTML, and a sanitiser is cheaper than reviewing every
 * future page for markup. After sanitising, headings get GitHub-style ids (so
 * `#anchor` links work the same here and in the GitHub wiki) and links that
 * leave this site open in a new tab without handing it `window.opener`.
 */

export interface HelpHeading {
  id: string;
  text: string;
  level: number;
}

export interface RenderedHelp {
  html: string;
  headings: HelpHeading[];
}

const markdown = new Marked({ gfm: true });

/** GitHub's heading anchor: lower-case, punctuation dropped, spaces → hyphens. */
export function headingId(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s_-]/gu, '')
    .replace(/\s/g, '-');
}

/** An http(s) link to another origin — it leaves QAIM. */
export function isExternalLink(href: string, origin: string): boolean {
  try {
    const url = new URL(href, origin);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.origin !== origin;
  } catch {
    return false;
  }
}

export function renderHelpMarkdown(
  source: string,
  origin: string = window.location.origin
): RenderedHelp {
  const html = markdown.parse(source, { async: false });
  const fragment = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    RETURN_DOM_FRAGMENT: true,
  });
  const doc = fragment.ownerDocument ?? document;

  const headings: HelpHeading[] = [];
  const used = new Map<string, number>();
  fragment.querySelectorAll('h1, h2, h3').forEach((heading) => {
    const text = heading.textContent ?? '';
    const base = headingId(text) || 'section';
    const count = used.get(base) ?? 0;
    used.set(base, count + 1);
    const id = count === 0 ? base : `${base}-${count}`;
    heading.id = id;
    headings.push({ id, text, level: Number(heading.tagName.slice(1)) });
  });

  fragment.querySelectorAll('a[href]').forEach((link) => {
    if (isExternalLink(link.getAttribute('href') ?? '', origin)) {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      link.classList.add('help-external');
      const hint = doc.createElement('span');
      hint.className = 'help-sr-only';
      hint.textContent = ' (opens in a new tab)';
      link.appendChild(hint);
    } else {
      link.removeAttribute('target');
    }
  });

  const container = doc.createElement('div');
  container.appendChild(fragment);
  return { html: container.innerHTML, headings };
}
